/**************************************************************************
 * Created on Tue Dec 16 2025
 *
 *  # Honeywell Code Usage Policy
 *
 * **Honeywell Source Code Usage Policy Classification:** Highly Confidential
 *
 * [comment]: # (Options for above are "Inner Source", "General Application", "Highly Confidential", "Export Controlled")
 *
 * **This repository has multiple classifications:** Yes
 *
 * [comment]: # (Options for above are "Yes" when subfolders contain a separate policy,"No" when the whole repo has the same policy)
 *
 * **Honeywell confidential - For internal use only.
 *
 * Property of and copyright ownership by Honeywell International Inc. and/or its affiliates.
 *
 * Use of source code must follow all Honeywell policies including the [HCE Source Code Access Policy](http://go.honeywell.com/hce-code-access-policy).
 *
 * External use or distribution prohibited without written approval per Honeywell policies**
 *
********************************************************************************/

import React, { useEffect } from "react";
import "./index.scss";
import DSSButton from "../../../../../ui/building-blocks/atoms/dss-button";
import { notifyError } from "../../../../../ui/dls/dls-snackbar/dls-snackbar";

type BroadcastMsgListProps = {
  dataList: any[];
  buttonLabel?: string;
  onClose?: () => void;
  Close?: () => void;
};

export const BroadcastMsgList = (props: BroadcastMsgListProps) =>
{
  const handleClose = props.onClose ?? props.Close ?? (() => undefined);
  const visibleMessages = (Array.isArray(props.dataList) ? props.dataList : []).filter((message: any) => {
    const webControl = message?.broadcastControls?.find((control: any) =>
      Number(control?.controlId) === 9 || String(control?.controlName ?? '').toLowerCase() === 'web'
    );
    return !webControl || String(webControl.controlFlag).toUpperCase() === 'Y';
  });

  const hasOnlyWebDisabledMessages = Array.isArray(props.dataList) &&
    props.dataList.length > 0 && visibleMessages.length === 0;

  useEffect(() => {
    if (hasOnlyWebDisabledMessages) handleClose();
  }, [hasOnlyWebDisabledMessages, handleClose]);

  const nodeToReactNode = (node: ChildNode, key: string): React.ReactNode => {
    if (node.nodeType === Node.TEXT_NODE) {
      return node.textContent;
    }

    if (node.nodeType !== Node.ELEMENT_NODE) {
      return null;
    }

    const element = node as HTMLElement;
    const tagName = element.tagName.toLowerCase();
    const props: Record<string, string | number> = { key };

    Array.from(element.attributes).forEach((attribute) => {
      props[attribute.name] = attribute.value;
    });

    const children = Array.from(element.childNodes).map((childNode, index) =>
      nodeToReactNode(childNode, `${key}-${index}`)
    );

    return React.createElement(tagName, props, ...children);
  };
 
  // Function to sanitize HTML by removing style tags and inline styles
  const messageDecryption = (encodedHTML: string): React.ReactNode[] => {
    try {
      let decodedHTML = atob(encodedHTML || "");
      
      // Parse HTML using DOMParser for proper sanitization
      const parser = new DOMParser();
      const doc = parser.parseFromString(decodedHTML, 'text/html');
      
      // Remove script tags
      const scripts = doc.querySelectorAll('script');
      scripts.forEach(script => script.remove());
      
      // Remove style tags
      const styles = doc.querySelectorAll('style');
      styles.forEach(style => style.remove());
      
      // Remove title tags
      const titles = doc.querySelectorAll('title');
      titles.forEach(title => title.remove());
      
      // Remove all event handler attributes and unwanted attributes
      const allElements = doc.querySelectorAll('*');
      allElements.forEach(element => {
        Array.from(element.attributes).forEach(attr => {
          // Remove event handlers (onclick, onload, etc.)
          if (attr.name.startsWith('on')) {
            element.removeAttribute(attr.name);
          }
          // Remove style and class attributes
          if (attr.name === 'style' || attr.name === 'class') {
            element.removeAttribute(attr.name);
          }
        });
      });

      return Array.from(doc.body.childNodes).map((node, index) =>
        nodeToReactNode(node, `broadcast-msg-${index}`)
      );
    } catch (error) {
      notifyError("Failed to decode message");
      return [];
    }
  };

  if (hasOnlyWebDisabledMessages) return null;


  return (
    <div className="broadcast-msg-list-main-container">
       <div className="docs-modal-header-box">
                Notifications
            </div>
      {/* BroadcastMsgList content */ }
       
      <div className="broadcast-msg-list-content">
        { visibleMessages.length > 0 ? (
          visibleMessages.map((msg, index) => {
            const titleCriteria = msg?.broadcastCriterias?.find((criteria: any) => criteria.criteriaName === 'title');
            const title = titleCriteria?.criteriaValue || '';
            
            return (
              <div key={ index } className="broadcast-msg-item">
                {title && (
                  <div style={{ marginBottom: '8px' }}>
                    <h3 style={{ margin: '0 0 8px 0', fontWeight: 'bold', fontSize: '16px' }}>
                      {title}
                    </h3>
                  </div>
                )}
                {/* Display message content */}
                <div>{messageDecryption(msg?.messageDesc)}</div>
              </div>
            );
          })
        ) : null }
      </div>
      <div className="footer">
        <DSSButton
          id="broadcast-msg-close-btn"
          label={props.buttonLabel || "Close"}
          variant="primary"
style={{height:"36px",width:"120px"}}
          onClick={ () =>
          {
            handleClose()
          } }
        >{props.buttonLabel || "Close"}</DSSButton>
      </div>
    </div>

  );
};
